import { useMemo } from 'react'
import { Box, Paper, Stack, Typography, LinearProgress } from '@mui/material'
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined'
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts'
import DashboardSearchableSelect from './DashboardSearchableSelect'
import { ChartLegend } from './RangeButtons'
import { DASHBOARD_COLORS, dashboardCardSx, shortChartLabel, formatCurrency, formatNumber } from './dashboardFormatters'

const ComparisonMetricRows = ({
  metrics,
  currentLabel,
  priorLabel,
}) => (
  <Stack spacing={2.25} sx={{ pt: 0.5 }}>
    {metrics.map(({ label, current, prior, format }) => {
      const max = Math.max(Number(current) || 0, Number(prior) || 0, 1)
      const rows = [
        { key: 'current', name: currentLabel, value: Number(current) || 0, color: DASHBOARD_COLORS.red },
        { key: 'prior', name: priorLabel, value: Number(prior) || 0, color: DASHBOARD_COLORS.gray },
      ]
      return (
        <Box key={label}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
              {format(current)} vs {format(prior)}
            </Typography>
          </Stack>
          <Stack spacing={0.75}>
            {rows.map((row) => (
              <Stack key={row.key} direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 76, flexShrink: 0, fontSize: '11px' }}>
                  {row.name}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: 12,
                    bgcolor: DASHBOARD_COLORS.bg,
                    borderRadius: '6px',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.max(4, (row.value / max) * 100)}%`,
                      height: '100%',
                      bgcolor: row.color,
                      borderRadius: '6px',
                      minWidth: row.value > 0 ? 4 : 0,
                      transition: 'width 0.35s ease',
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{ width: 52, flexShrink: 0, textAlign: 'right', fontSize: '11px' }}
                >
                  {format(row.value)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      )
    })}
  </Stack>
)

const PERIOD_OPTIONS = [
  { id: '7d', label: '7 Days' },
  { id: '30d', label: 'Monthly' },
  { id: '90d', label: 'Quarterly' },
]

const SectionTitle = ({ icon: Icon, title, children, stackFilters = false }) => (
  <Stack
    direction={stackFilters ? 'column' : 'row'}
    justifyContent="space-between"
    alignItems={stackFilters ? 'stretch' : 'flex-start'}
    flexWrap={stackFilters ? undefined : 'wrap'}
    gap={stackFilters ? 1.25 : 1}
    sx={{ mb: 2 }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, minWidth: 0 }}>
      <Icon sx={{ fontSize: 20, color: DASHBOARD_COLORS.red, flexShrink: 0 }} />
      <Typography variant="subtitle1" fontWeight={700} noWrap>{title}</Typography>
    </Stack>
    {children && (
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{
          width: stackFilters ? '100%' : undefined,
          minWidth: stackFilters ? 0 : undefined,
          flex: stackFilters ? undefined : '1 1 auto',
          justifyContent: stackFilters ? 'flex-start' : { xs: 'flex-start', sm: 'flex-end' },
        }}
      >
        {children}
      </Stack>
    )}
  </Stack>
)

export const CategorySalesSection = ({
  data,
  filters,
  locations,
  onLocationSearch,
  locationLoading,
  setFilter,
  loading,
}) => (
  <Paper sx={{ ...dashboardCardSx, height: '100%', overflow: 'hidden', minWidth: 0 }}>
    <SectionTitle icon={DonutLargeOutlinedIcon} title="Category Sales">
      <DashboardSearchableSelect
        label="Period"
        options={PERIOD_OPTIONS}
        value={filters.categoryPeriod}
        onChange={(opt) => setFilter({ categoryPeriod: opt?.id || '7d' })}
        pill
      />
      <DashboardSearchableSelect
        label="Outlet"
        options={locations}
        value={filters.categoryOutletId || filters.locationId}
        onChange={(opt) => setFilter({ categoryOutletId: opt?.id || '' })}
        onSearch={onLocationSearch}
        loading={locationLoading}
        pill
      />
    </SectionTitle>
    {loading && <LinearProgress sx={{ mb: 1 }} />}
    <Stack
      direction="row"
      spacing={{ xs: 1.5, md: 2.5 }}
      alignItems="flex-start"
      sx={{ minWidth: 0 }}
    >
      <Box
        sx={{
          width: { xs: 110, sm: 130, md: 140 },
          height: { xs: 110, sm: 130, md: 140 },
          flexShrink: 0,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data?.items || []}
              dataKey="value"
              nameKey="label"
              innerRadius="55%"
              outerRadius="85%"
              paddingAngle={2}
            >
              {(data?.items || []).map((item, i) => (
                <Cell
                  key={item.label}
                  fill={DASHBOARD_COLORS.chartPalette[i % DASHBOARD_COLORS.chartPalette.length]}
                />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Stack spacing={1} sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
        {(data?.items || []).map((item, i) => (
          <Stack
            key={item.label}
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
            sx={{ minWidth: 0 }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '2px',
                  flexShrink: 0,
                  mt: '4px',
                  bgcolor: DASHBOARD_COLORS.chartPalette[i % DASHBOARD_COLORS.chartPalette.length],
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.35,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {item.label}
              </Typography>
            </Stack>
            <Typography variant="body2" fontWeight={700} sx={{ flexShrink: 0, pl: 0.5 }}>
              {item.pct}%
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  </Paper>
)

export const SalesComparisonSection = ({ data, filters, setFilter, loading }) => {
  const currentLabel = data?.currentLabel || 'This period'
  const priorLabel = data?.priorLabel || 'Prior period'
  const salesIndex = (data?.labels || []).indexOf('Sales')
  const ordersIndex = (data?.labels || []).indexOf('Orders')
  const salesCurrent = salesIndex >= 0 ? data?.current?.[salesIndex] : data?.current?.[0]
  const salesPrior = salesIndex >= 0 ? data?.prior?.[salesIndex] : data?.prior?.[0]
  const ordersCurrent = ordersIndex >= 0 ? data?.current?.[ordersIndex] : data?.current?.[1]
  const ordersPrior = ordersIndex >= 0 ? data?.prior?.[ordersIndex] : data?.prior?.[1]

  return (
    <Paper sx={{ ...dashboardCardSx, height: '100%' }}>
      <SectionTitle icon={CompareArrowsOutlinedIcon} title="Sales Comparison">
        <DashboardSearchableSelect
          label="Period"
          options={[
            { id: 'today', label: 'Today vs Yesterday' },
            { id: 'week', label: 'This Week vs Last' },
            { id: 'month', label: 'This Month vs Last' },
            { id: 'quarter', label: 'This Quarter vs Last' },
          ]}
          value={filters.comparisonPeriod}
          onChange={(opt) => setFilter({ comparisonPeriod: opt?.id || 'week' })}
          pill
        />
      </SectionTitle>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <ComparisonMetricRows
        currentLabel={currentLabel}
        priorLabel={priorLabel}
        metrics={[
          { label: 'Sales', current: salesCurrent, prior: salesPrior, format: formatCurrency },
          { label: 'Orders', current: ordersCurrent, prior: ordersPrior, format: formatNumber },
        ]}
      />
      <ChartLegend
        items={[
          { label: currentLabel, color: DASHBOARD_COLORS.red },
          { label: priorLabel, color: DASHBOARD_COLORS.gray },
        ]}
      />
    </Paper>
  )
}

export const StockHealthSection = ({ data, filters, setFilter, loading }) => {
  const chartData = useMemo(() => {
    const labels = data?.labels || []
    const maxLabelLen = labels.length > 6 ? 10 : 14
    return labels.map((label, i) => ({
      label: (label || '').trim() || 'Unassigned',
      shortLabel: shortChartLabel((label || '').trim() || 'Unassigned', maxLabelLen),
      Healthy: data?.healthy?.[i] || 0,
      NearExpiry: data?.nearExpiry?.[i] || 0,
      Expired: data?.expired?.[i] || 0,
    }))
  }, [data?.labels, data?.healthy, data?.nearExpiry, data?.expired])

  return (
    <Paper sx={{ ...dashboardCardSx, height: '100%' }}>
      <SectionTitle icon={Inventory2OutlinedIcon} title="Stock Health">
        <DashboardSearchableSelect
          label="View"
          options={[
            { id: 'category', label: 'By Category' },
            { id: 'outlet', label: 'By Outlet' },
          ]}
          value={filters.stockView}
          onChange={(opt) => setFilter({ stockView: opt?.id || 'category' })}
          pill
        />
      </SectionTitle>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Box sx={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 56 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
            <XAxis
              dataKey="shortLabel"
              interval={0}
              tick={{ fontSize: 10, fill: '#888882' }}
              angle={-32}
              textAnchor="end"
              height={56}
            />
            <YAxis tick={{ fontSize: 10, fill: '#888882' }} width={42} />
            <Tooltip
              labelFormatter={(_label, payload) => payload?.[0]?.payload?.label || _label}
            />
            <Bar dataKey="Healthy" stackId="a" fill={DASHBOARD_COLORS.green} radius={[0, 0, 3, 3]} />
            <Bar dataKey="NearExpiry" stackId="a" fill={DASHBOARD_COLORS.amber} />
            <Bar dataKey="Expired" stackId="a" fill={DASHBOARD_COLORS.red} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <ChartLegend
        items={[
          { label: 'Healthy', color: DASHBOARD_COLORS.green },
          { label: 'Near Expiry', color: DASHBOARD_COLORS.amber },
          { label: 'Expired', color: DASHBOARD_COLORS.red },
        ]}
      />
    </Paper>
  )
}
